import React from "react";
import { api, DailyBar } from "../api";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/** Format YYYY-MM-DD as New York date (handles EST/EDT). */
function dateNY(isoOrDate: string | Date) {
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(d).replace(/\u202f|\u00a0/g, "");
}

/** USD formatter */
const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

/** Read a CSS var with a fallback. */
function cssVar(name: string, fallback: string) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Compute NY “today” as YYYY-MM-DD string. */
function todayNY() {
  return dateNY(new Date());
}

/** YYYY-MM-DD helper */
function ymd(y: number, m: number, d: number) {
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

/** Date math helpers (safe for boundaries because we only ever format back via NY) */
function addDaysUTC(d: Date, delta: number) {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + delta);
  return x;
}

/** Get parts for “now” in NY time. */
function nyParts(d = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
    weekday: "short",
  }).formatToParts(d);
  const get = (t: string) => parts.find(p => p.type === t)?.value!;
  const y = Number(get("year"));
  const m = Number(get("month"));
  const day = Number(get("day"));
  const wdName = get("weekday"); // Sun/Mon/Tue…
  const map: Record<string, number> = { Sun:0, Mon:1, Tue:2, Wed:3, Thu:4, Fri:5, Sat:6 };
  return { y, m, day, wd: map[wdName] ?? 0 };
}

/** Quick-range preset -> {from,to} (YYYY-MM-DD strings, inclusive). */
function rangePreset(which: RangeKey): { from?: string; to?: string } {
  const now = new Date();
  const { y, m, day, wd } = nyParts(now);
  const today = todayNY();

  if (which === "TODAY") return { from: today, to: today };

  if (which === "WEEK") {
    // “This Week” starting Monday in NY
    const mondayOffset = (wd + 6) % 7; // convert Sun=0..Sat=6 to Mon=0..Sun=6
    const fromDate = addDaysUTC(now, -mondayOffset);
    return { from: dateNY(fromDate) };
  }

  if (which === "MONTH") {
    return { from: ymd(y, m, 1) };
  }

  if (which === "LAST_MONTH") {
    const pm = m === 1 ? 12 : m - 1;
    const py = m === 1 ? y - 1 : y;
    const lastDay = new Date(y, m - 1, 0).getDate(); // days in previous month
    return { from: ymd(py, pm, 1), to: ymd(py, pm, lastDay) };
  }

  if (which === "YEAR") {
    return { from: ymd(y, 1, 1) };
  }

  if (which === "LAST_YEAR") {
    const ly = y - 1;
    return { from: ymd(ly, 1, 1), to: ymd(ly, 12, 31) };
  }

  return {};
}

type RangeKey = "TODAY" | "WEEK" | "MONTH" | "LAST_MONTH" | "YEAR" | "LAST_YEAR" | "CUSTOM";

export default function DailyChart({
  symbol,
  days = 460, // load enough history to cover presets
}: {
  symbol: string;
  days?: number;
}) {
  const [rows, setRows] = React.useState<DailyBar[] | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  // Range UI state
  const [range, setRange] = React.useState<RangeKey>("MONTH");
  const [customFrom, setCustomFrom] = React.useState<string>("");
  const [customTo, setCustomTo] = React.useState<string>("");

  const load = React.useCallback(async () => {
    try {
      setRows(await api.daily(symbol, days));
      setErr(null);
    } catch (e: any) {
      setErr(String(e));
    }
  }, [symbol, days]);

  React.useEffect(() => { load(); }, [load]);

  if (err) return <div className="card bad">{err}</div>;
  if (!rows) return <div className="card">Loading daily…</div>;

  // Convert all bars to NY calendar date + close
  const bars = rows.map(r => ({ d: dateNY(r.ts), r }));

  // Determine active filter
  let { from, to } = range === "CUSTOM" ? { from: customFrom || undefined, to: customTo || undefined }
                                        : rangePreset(range);

  // Filter rows (inclusive). If no from/to, show all fetched.
  const inRange = (d: string) => (!from || d >= from) && (!to || d <= to);
  const filtered = bars.filter(b => inRange(b.d));
  const labels = filtered.map(b => b.d);
  const data = filtered.map(b => b.r.close);

  // Theme colors
  const colorBarDefault = cssVar("--chart-bar", "#9fa8da");
  const colorUp = cssVar("--good", "#2e7d32");
  const colorDown = cssVar("--bad", "#c62828");
  const axisColor = cssVar("--chart-axis", "#a8bdcf");
  const gridColor = cssVar("--chart-grid", "#243141");
  const tooltipBg = cssVar("--tooltip-bg", "#0f141b");
  const text = cssVar("--text", "#e7edf3");
  const border = cssVar("--border", "#1f2a36");

  // Per-bar color vs previous close within the filtered window
  const barColors = data.map((v, i) => (i === 0 ? colorBarDefault : v >= data[i - 1] ? colorUp : colorDown));

  return (
    <div className="card">
      <div className="row">
        <h2 style={{ margin: 0 }}>{symbol} — Daily Close</h2>
        <button onClick={load}>refresh</button>
      </div>

      {/* Quick range chips + custom inputs */}
      <div className="chips">
        {[
          ["TODAY","Today"],
          ["WEEK","This Week"],
          ["MONTH","This Month"],
          ["LAST_MONTH","Last Month"],
          ["YEAR","This Year"],
          ["LAST_YEAR","Last Year"],
        ].map(([k, label]) => (
          <button
            key={k}
            className={"chip" + (range === (k as RangeKey) ? " active" : "")}
            onClick={() => setRange(k as RangeKey)}
          >
            {label}
          </button>
        ))}
        <span style={{ marginLeft: "auto" }} />
        <label className="muted" style={{ alignSelf: "center" }}>From:</label>
        <input
          type="date"
          value={customFrom}
          onChange={(e) => { setCustomFrom(e.target.value); setRange("CUSTOM"); }}
        />
        <label className="muted" style={{ alignSelf: "center" }}>To:</label>
        <input
          type="date"
          value={customTo}
          onChange={(e) => { setCustomTo(e.target.value); setRange("CUSTOM"); }}
        />
        <button className="chip" onClick={() => { setCustomFrom(""); setCustomTo(""); setRange("MONTH"); }}>
          Clear
        </button>
      </div>

      <div className="chart">
        <Bar
          data={{
            labels,
            datasets: [{
              label: "Close",
              data,
              backgroundColor: barColors,
              borderColor: barColors,
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.9,
              categoryPercentage: 0.9,
            }],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            plugins: {
              legend: { display: true, labels: { color: text } },
              tooltip: {
                backgroundColor: tooltipBg,
                borderColor: border,
                borderWidth: 1,
                titleColor: text,
                bodyColor: text,
                callbacks: {
                  title: (items) => (items[0] ? `NY ${items[0].label}` : ""),
                  label: (ctx) => {
                    const i = ctx.dataIndex;
                    const r = filtered[i].r;
                    return [
                      `Close: ${usd.format(r.close)}`,
                      `O ${usd.format(r.open)}  H ${usd.format(r.high)}`,
                      `L ${usd.format(r.low)}`,
                    ];
                  },
                },
              },
            },
            scales: {
              x: { ticks: { maxRotation: 0, color: axisColor }, grid: { color: gridColor, drawBorder: false } },
              y: {
                ticks: { color: axisColor, callback: (v) => usd.format(Number(v)) },
                grid: { color: gridColor, drawBorder: false },
              },
            },
          }}
        />
      </div>

      <small>
        Oldest → newest. Dates shown in New York time. Bar color indicates change vs prior close.
      </small>
    </div>
  );
}
